import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Fade,
  Grow,
  Slide
} from '@mui/material';
import {
  TrendingUp,
  Download,
  DirectionsRun,
  DirectionsWalk,
  BugReport,
  ArrowBack,
  Assessment,
  EmojiEvents,
  Print
} from '@mui/icons-material';
import { AssessmentMetadata } from '../types';
import { QuestionData } from '../services/questionService';
import { useLocation, useNavigate } from 'react-router-dom';
import './ContentBoxes.css';

// Determine maturity level based on percentage score
const getMaturityLevel = (score: number, maxScore: number): 'Crawl' | 'Walk' | 'Run' => {
  const percentage = (score / maxScore) * 100;
  if (percentage < 30) return 'Crawl';
  if (percentage < 60) return 'Walk';
  return 'Run';
};

// Get color based on maturity level
const getMaturityColor = (level: 'Crawl' | 'Walk' | 'Run') => {
  switch (level) {
    case 'Crawl': return '#ff9800'; // Orange
    case 'Walk': return '#2196f3';  // Blue
    case 'Run': return '#4caf50';   // Green
  }
};

// Get icon based on maturity level
const getMaturityIcon = (level: 'Crawl' | 'Walk' | 'Run') => {
  switch (level) {
    case 'Crawl': return <BugReport color="error" />;
    case 'Walk': return <DirectionsWalk color="warning" />;
    case 'Run': return <DirectionsRun color="success" />;
  }
};

const Results: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { answers, questions, comments, showScores, assessmentData } = location.state || {
    answers: {} as Record<string, Record<string, number>>,
    questions: [] as QuestionData[],
    comments: {} as Record<string, string>,
    showScores: process.env.REACT_APP_SHOW_SCORES === 'false' ? false : true,
    assessmentData: {},
  };

  // Add debug logging
  // console.log('DEBUG: Questions received:', questions.length);
  // console.log('DEBUG: Answers received:', Object.keys(answers).length);
  // console.log('DEBUG: Sample question:', questions[0]);
  // console.log('DEBUG: Sample answer:', answers[questions[0]?.question]);
  
  // metadata is undefined here; if passing through as state then need to consistently pass
  //  it between the domain and questionnaire pages too.
  //  The "domain" does not handle metadata in state and thus does not pass it on either.
  //  Simply restore the metadata from from localStorage
  const metadataJson = localStorage.getItem('metadata');
  let metadata: AssessmentMetadata =  metadataJson ? JSON.parse(metadataJson) : {
    description: '',
    contactName: '',
    contactEmail: '',
    date: '',
    teamName: '',
    cloudAccounts: []
  };

  // reset the metadata to that of the results
  metadata.date = new Date().toISOString();

  console.log('WA DEBUG - metadata: ', metadata);

  const handleBackToQuestionnaire = () => {
    navigate('/questionnaire', { state: { questions, answers } });
  };

  // Calculate scores for each category
  const categoryScores = {
    knowledge: 0,
    process: 0,
    metrics: 0,
    adoption: 0,
    automation: 0
  };

  let questionCount = 0;
  let totalScore = 0;
  let maxPossibleScore = 0;

  questions.forEach((question: QuestionData) => {
    const questionAnswers = answers[question.question] || {};

    // Process all 5 categories (all initialized to 0)
    const categories = ['knowledge', 'process', 'metrics', 'adoption', 'automation'];
    categories.forEach(category => {
      const score = questionAnswers[category] || 0;
      categoryScores[category as keyof typeof categoryScores] += score;
      totalScore += score;
      maxPossibleScore += 5; // Maximum score per category is 5
    });

    questionCount++;
  });

  // Calculate averages
  Object.keys(categoryScores).forEach(key => {
    categoryScores[key as keyof typeof categoryScores] /= questionCount;
  });

  // Calculate overall score and maturity
  const overallScore = totalScore / maxPossibleScore * 5; // Convert to 0-5 scale for display
  const overallMaturity = getMaturityLevel(totalScore, maxPossibleScore);

  // Find improvement areas (categories with lowest scores)
  const improvementAreas = Object.entries(categoryScores)
    .sort(([, scoreA], [, scoreB]) => scoreA - scoreB)
    .slice(0, 3);

  return (
    <Box sx={{
      maxWidth: 1200,
      mx: 'auto',
      p: { xs: 2, md: 4 },
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Header Actions */}
      <Fade in={true} timeout={600}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBackToQuestionnaire}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              border: '2px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
              background: 'white',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: 'primary.dark',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
              }
            }}
          >
            Back to Assessment
          </Button>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => {
                const dataStr = JSON.stringify({ answers, comments, questions, metadata, assessmentData }, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'assessment-answers.json';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 4px 12px rgba(118, 75, 162, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(118, 75, 162, 0.4)'
                }
              }}
            >
              Download JSON
            </Button>

            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={() => window.print()}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                }
              }}
            >
              Print Results
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Overall Summary */}
      { showScores && (
        <Grow in={true} timeout={800}>
          <Paper className="results-summary-box" sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
            }
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <EmojiEvents sx={{
                    fontSize: 32,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                    letterSpacing: '-0.5px',
                    lineHeight: 1.2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    position: 'relative'
                  }}
                >
                  Assessment Results
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}>
                {location.state?.metadata?.teamName && (
                  <Typography variant="subtitle1" fontWeight={500}>
                    {location.state.metadata.teamName}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {new Date(location.state?.metadata?.date || new Date()).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: { xs: 'column', md: 'row' },
              mt: 4,
              gap: 4
            }}>
              <Box sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 260,
                width: { xs: '100%', md: '50%' }
              }}>
                {/* Background circle */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={220}
                  thickness={6}
                  sx={{
                    color: 'rgba(102, 126, 234, 0.08)',
                    position: 'absolute',
                    left: 0,
                  }}
                />
                {/* Progress circle with animation */}
                <CircularProgress
                  variant="determinate"
                  value={(overallScore / 5) * 100}
                  size={220}
                  thickness={6}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    background: `linear-gradient(135deg, ${getMaturityColor(overallMaturity)} 0%, ${getMaturityColor(overallMaturity)}dd 100%)`,
                    borderRadius: '50%',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                      stroke: `url(#gradient-${overallMaturity})`,
                      filter: 'drop-shadow(0 4px 12px rgba(102, 126, 234, 0.3))',
                      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: 'dash 2s ease-in-out'
                    },
                    '@keyframes dash': {
                      '0%': {
                        strokeDasharray: '0, 1000'
                      }
                    }
                  }}
                />
                {/* SVG gradient definitions */}
                <svg width="0" height="0">
                  <defs>
                    <linearGradient id={`gradient-${overallMaturity}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={getMaturityColor(overallMaturity)} />
                      <stop offset="100%" stopColor={`${getMaturityColor(overallMaturity)}dd`} />
                    </linearGradient>
                  </defs>
                </svg>

                {/* Center content */}
                <Box sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1.5,
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${getMaturityColor(overallMaturity)}15 0%, ${getMaturityColor(overallMaturity)}25 100%)`,
                    border: `2px solid ${getMaturityColor(overallMaturity)}`,
                    boxShadow: `0 8px 24px ${getMaturityColor(overallMaturity)}30`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: `0 12px 32px ${getMaturityColor(overallMaturity)}40`
                    }
                  }}>
                    {getMaturityIcon(overallMaturity)}
                    <Typography variant="h3" component="div" sx={{
                      fontWeight: 800,
                      color: getMaturityColor(overallMaturity),
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                      textShadow: `0 2px 8px ${getMaturityColor(overallMaturity)}20`
                    }}>
                      {overallMaturity}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                    fontSize: '1.1rem'
                  }}>
                    Score: {overallScore.toFixed(1)}/5
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                flex: 1,
                width: { xs: '100%', md: '50%' },
                pl: { md: 4 }
              }}>
                <Typography variant="h6" gutterBottom sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: 'text.primary',
                  mb: 2
                }}>
                  Overall Summary
                </Typography>
                <Typography variant="body1" paragraph sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  fontSize: '1rem'
                }}>
                  {overallMaturity === 'Crawl' ? (
                    "Your organization is in the early stages of adoption. Focus on building awareness, establishing basic processes, and collecting fundamental metrics."
                  ) : overallMaturity === 'Walk' ? (
                    "Your organization has established practices, but there's room for improvement. Work on optimizing processes, enhancing metrics, and expanding adoption."
                  ) : (
                    "Your organization demonstrates advanced maturity. Continue to innovate, automate, and optimize while sharing knowledge with the wider community."
                  )}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{
              my: 4,
              borderColor: 'rgba(102, 126, 234, 0.1)',
              borderWidth: 1
            }} />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mt: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Team Details
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Team/Department
                  </Typography>
                  <Typography variant="body1">
                    {location.state?.metadata?.teamName || 'Not specified'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Contact
                  </Typography>
                  <Typography variant="body1">
                    {location.state?.metadata?.contactName || 'Not specified'}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    {location.state?.metadata?.contactEmail || 'No email provided'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Assessment Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(location.state?.metadata?.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 2 }}>
                {location.state?.metadata?.description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Assessment Description
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {location.state?.metadata?.description}
                    </Typography>
                  </Box>
                )}

                {location.state?.metadata?.cloudAccounts && location.state?.metadata?.cloudAccounts.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Cloud Accounts ({location.state?.metadata?.cloudAccounts.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {location.state?.metadata?.cloudAccounts.map((account: { id: string; name: string; provider: string }) => (
                        <Chip
                          key={account.id}
                          label={`${account.name} (${account.provider})`}
                          size="small"
                          variant="filled"
                          sx={{
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                            '&:hover': {
                              bgcolor: 'primary.main',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grow>
      )}

      {/* Recommendations */}
      { showScores && (
        <Slide direction="up" in={true} timeout={1000}>
          <Paper className="improvement-box" sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease-in-out'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
              <Box sx={{
                p: 1,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUp sx={{
                  fontSize: 28,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
              </Box>
              <Typography variant="h5" sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Areas of Improvement
              </Typography>
            </Box>
            <Divider sx={{ mb: 4, borderColor: 'rgba(102, 126, 234, 0.1)' }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              {/* Focus Areas */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Focus Areas
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {improvementAreas.map(([category, score]) => (
                    <Chip
                      key={category}
                      label={`${category.charAt(0).toUpperCase() + category.slice(1)}: ${score.toFixed(1)}/5`}
                      sx={{
                        py: 2.5,
                        px: 1.5,
                        backgroundColor: 'rgba(102, 126, 234, 0.08)',
                        color: '#667eea',
                        border: '2px solid #667eea',
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.15)',
                          transform: 'translateY(-4px) scale(1.05)',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

              {/* Maturity by Category */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Maturity by Category
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {Object.entries(categoryScores).map(([category, score], index) => (
                    <Fade in={true} timeout={1200 + (index * 100)} key={category}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{
                          textTransform: 'capitalize',
                          minWidth: 100,
                          fontWeight: 600,
                          color: 'text.primary'
                        }}>
                          {category}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: 300 }}>
                          <Box sx={{
                            width: '100%',
                            height: 10,
                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                            borderRadius: 10,
                            mr: 2,
                            overflow: 'hidden',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                          }}>
                            <Box sx={{
                              width: `${(score / 5) * 100}%`,
                              height: '100%',
                              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: 10,
                              transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              boxShadow: '0 0 8px rgba(102, 126, 234, 0.4)'
                            }} />
                          </Box>
                          <Typography variant="body2" sx={{
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            minWidth: 60,
                            textAlign: 'right'
                          }}>
                            {score.toFixed(1)}/5
                          </Typography>
                        </Box>
                      </Box>
                    </Fade>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Slide>
      )}


      {/* Detailed Results */}
      <Fade in={true} timeout={1400}>
        <Paper className="detailed-results-box" sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
            <Box sx={{
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Assessment sx={{
                fontSize: 28,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }} />
            </Box>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Detailed Results
            </Typography>
          </Box>
          <Divider sx={{ mb: 4, borderColor: 'rgba(102, 126, 234, 0.1)' }} />
          <List>
            {questions.map((question: QuestionData, index: number) => {
              const questionAnswers = answers[question.id] || {};
              const categories = ['knowledge', 'process', 'metrics', 'adoption', 'automation'];
              const totalQuestionScore = categories.reduce((sum, category) => {
                return sum + questionAnswers[category];
              }, 0);
              const questionScore = totalQuestionScore / categories.length;
              const maturity = getMaturityLevel(questionScore, 5);
              const questionComment = comments?.[question.id];

              return (
                <Grow in={true} timeout={1600 + (index * 50)} key={index}>
                  <ListItem
                    className="question-item"
                    sx={{
                      mb: 3,
                      p: 3,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${getMaturityColor(maturity)}05 0%, ${getMaturityColor(maturity)}10 100%)`,
                      border: `2px solid ${getMaturityColor(maturity)}40`,
                      boxShadow: `0 4px 12px ${getMaturityColor(maturity)}15`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${getMaturityColor(maturity)}25`,
                        border: `2px solid ${getMaturityColor(maturity)}60`
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getMaturityIcon(maturity)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {question.domain.name} - {question.capability.name} - {question.question}
                          </Typography>
                          <Chip
                            label={`${questionScore.toFixed(1)}/5`}
                            size="small"
                            sx={{
                              backgroundColor: getMaturityColor(maturity),
                              color: 'white',
                              border: `2px solid ${getMaturityColor(maturity)}`,
                              fontWeight: 700,
                              px: 1,
                              boxShadow: `0 2px 8px ${getMaturityColor(maturity)}30`
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {categories.map((category) => {
                            const score = questionAnswers[category];
                            return (
                              <Chip
                                key={category}
                                label={`${category.charAt(0).toUpperCase() + category.slice(1)}: ${score}`}
                                size="small"
                                sx={{
                                  mr: 1,
                                  mt: 1,
                                  py: 1.5,
                                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                  color: '#667eea',
                                  border: '2px solid rgba(102, 126, 234, 0.3)',
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              />
                            );
                          })}
                          {questionComment && showScores && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Comments:
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {questionComment}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Grow>
              );
            })}
          </List>
        </Paper>
      </Fade>
    </Box>
  );
};


export default Results;

